import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useGetModel } from '../../../../../api/routes/models'
import {
    useGetDashboards,
    useCreateDashboard,
    useDeleteDashboard,
    useSetDefaultDashboard,
    Dashboard,
} from '../../../../../api/routes/dashboards'
import {
    ArrowLeft,
    Plus,
    Star,
    MoreVertical,
    Pencil,
    Trash2,
    Copy,
    LayoutDashboard,
} from 'lucide-react'
import { toast } from 'react-toastify'
import { Button } from '../../../../../components/ui/button'
import { Badge } from '../../../../../components/ui/badge'
import { Input } from '../../../../../components/ui/input'
import { Label } from '../../../../../components/ui/label'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
} from '../../../../../components/ui/dialog'
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from '../../../../../components/ui/dropdown-menu'

export const ModelDashboardsRoute = () => {
    const { id: modelId } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { data: model, isLoading: modelLoading } = useGetModel(modelId!)
    const { data: dashboards, isLoading } = useGetDashboards(modelId!)

    const createDashboard = useCreateDashboard()
    const deleteDashboard = useDeleteDashboard()
    const setDefault = useSetDefaultDashboard()

    const [createOpen, setCreateOpen] = useState(false)
    const [newName, setNewName] = useState('')

    const handleCreate = async () => {
        const name = newName.trim()
        if (!name) return
        try {
            const created = await createDashboard.mutateAsync({ modelId: modelId!, name })
            toast.success('Dashboard created')
            setCreateOpen(false)
            setNewName('')
            navigate(`/models/${modelId}/dashboards/${created.id}`)
        } catch {
            toast.error('Failed to create dashboard')
        }
    }

    const handleDuplicate = async (dashboard: Dashboard) => {
        try {
            await createDashboard.mutateAsync({
                modelId: modelId!,
                name: `${dashboard.name} (copy)`,
                widgets: dashboard.widgets,
            })
            toast.success('Dashboard duplicated')
        } catch {
            toast.error('Failed to duplicate dashboard')
        }
    }

    const handleSetDefault = async (dashboard: Dashboard) => {
        try {
            await setDefault.mutateAsync(dashboard.id)
            toast.success(`"${dashboard.name}" is now the default`)
        } catch {
            toast.error('Failed to set default')
        }
    }

    const handleDelete = async (dashboard: Dashboard) => {
        if (!confirm(`Delete dashboard "${dashboard.name}"?`)) return
        try {
            await deleteDashboard.mutateAsync(dashboard.id)
            toast.success('Dashboard deleted')
        } catch {
            toast.error('Failed to delete dashboard')
        }
    }

    if (modelLoading) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                Loading...
            </div>
        )
    }

    if (!model) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                Model not found.
            </div>
        )
    }

    return (
        <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
            <div className="mb-6">
                <Link
                    to={`/models/${modelId}`}
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back to {model.name}
                </Link>
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h1 className="text-xl font-semibold text-foreground">
                            Dashboards
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Live dashboard layouts for {model.name} devices. The
                            default is what users see on the device live page.
                        </p>
                    </div>
                    {dashboards && dashboards.length > 0 && (
                        <Button onClick={() => setCreateOpen(true)}>
                            <Plus className="h-4 w-4 mr-1.5" />
                            New Dashboard
                        </Button>
                    )}
                </div>
            </div>

            {isLoading ? (
                <div className="text-sm text-muted-foreground">Loading...</div>
            ) : !dashboards || dashboards.length === 0 ? (
                <div className="rounded-lg border border-dashed bg-card p-10 text-center">
                    <LayoutDashboard className="h-8 w-8 mx-auto text-muted-foreground/50" />
                    <h3 className="mt-3 text-sm font-medium text-foreground">
                        No dashboards yet
                    </h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                        Create one to design what this model's devices show live.
                    </p>
                    <Button className="mt-4" onClick={() => setCreateOpen(true)}>
                        <Plus className="h-4 w-4 mr-1.5" />
                        New Dashboard
                    </Button>
                </div>
            ) : (
                <div className="grid gap-3">
                    {dashboards.map((dashboard) => (
                        <div
                            key={dashboard.id}
                            className="group flex items-center justify-between rounded-lg border bg-card p-4 hover:border-primary/30 hover:shadow-sm transition-all"
                        >
                            <Link
                                to={`/models/${modelId}/dashboards/${dashboard.id}`}
                                className="flex items-center gap-3 flex-1 min-w-0"
                            >
                                <div className="rounded-md bg-muted p-2.5 text-muted-foreground group-hover:text-primary group-hover:bg-primary/10 transition-colors">
                                    <LayoutDashboard className="h-5 w-5" />
                                </div>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-medium text-sm text-foreground truncate">
                                            {dashboard.name}
                                        </h3>
                                        {dashboard.isDefault && (
                                            <Badge
                                                variant="secondary"
                                                className="gap-1"
                                            >
                                                <Star className="h-3 w-3 fill-current" />
                                                Default
                                            </Badge>
                                        )}
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        {dashboard.widgets?.length ?? 0} widget
                                        {(dashboard.widgets?.length ?? 0) === 1
                                            ? ''
                                            : 's'}
                                    </p>
                                </div>
                            </Link>

                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon">
                                        <MoreVertical className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                        onClick={() =>
                                            navigate(
                                                `/models/${modelId}/dashboards/${dashboard.id}`
                                            )
                                        }
                                    >
                                        <Pencil className="h-4 w-4 mr-2" />
                                        Edit
                                    </DropdownMenuItem>
                                    {!dashboard.isDefault && (
                                        <DropdownMenuItem
                                            onClick={() =>
                                                handleSetDefault(dashboard)
                                            }
                                        >
                                            <Star className="h-4 w-4 mr-2" />
                                            Set as default
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem
                                        onClick={() =>
                                            handleDuplicate(dashboard)
                                        }
                                    >
                                        <Copy className="h-4 w-4 mr-2" />
                                        Duplicate
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        className="text-destructive focus:text-destructive"
                                        onClick={() => handleDelete(dashboard)}
                                    >
                                        <Trash2 className="h-4 w-4 mr-2" />
                                        Delete
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>New Dashboard</DialogTitle>
                        <DialogDescription>
                            Give the dashboard a name. You'll design its layout
                            next.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label htmlFor="dashboard-name">Name</Label>
                        <Input
                            id="dashboard-name"
                            value={newName}
                            placeholder="e.g. Overview"
                            onChange={(e) => setNewName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleCreate()
                            }}
                            autoFocus
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setCreateOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreate}
                            disabled={
                                !newName.trim() || createDashboard.isPending
                            }
                        >
                            Create
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
